import { Component } from '@angular/core';
import { take } from "rxjs/operators";
import { map, Observable, withLatestFrom } from "rxjs";
import { FileType } from "@models/file-type";
import { DocViewerStore } from "@store/legacy/doc-viewer.store";
import { AppStore } from "@store/legacy/app.store";
import { NotificationManager } from "@managers/notification.manager";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { DocService } from "@services/doc.service";
import { ReviewManager } from "@managers/review.manager";
import { TranslationUploadStrategy } from "@utilities/manual-upload-strategies/translation-upload-strategy";
import { ManualUploadDialogComponent } from '../../dialogs/manual-upload-dialog/manual-upload-dialog.component';
import { ManualUploadDialogModel } from '../../dialogs/manual-upload-dialog/manual-upload-dialog.model';
import { OkCancelDialogModel } from '../../dialogs/ok-cancel-dialog-new/ok-cancel-dialog.model';
import { OkCancelDialogNewComponent } from '../../dialogs/ok-cancel-dialog-new/ok-cancel-dialog-new.component';
import { Store } from '@ngrx/store';
import { documentViewActions } from '@store/doc-viewer/document-view/document-view.actions';

@Component({
    selector: 'lex-translate-action-panel',
    templateUrl: './translate-action-panel.component.html',
    styleUrls: ['./translate-action-panel.component.scss'],
    standalone: false
})
export class TranslateActionPanelComponent {
	public currentDocument$ = this.docViewerStore.currentDocument$;

	constructor(
		private store: Store,
		private appStore: AppStore,
		private docViewerStore: DocViewerStore,
		private notificationManager: NotificationManager,
		private matDialog: MatDialog,
		private docService: DocService,
		private reviewManager: ReviewManager,
		private translationUploadStrategy: TranslationUploadStrategy
	) {
	}

	public canDeleteTranslation$: Observable<boolean> = this.docViewerStore.simpleState$.pipe(
		withLatestFrom(this.appStore.appState$),
		map(([snapshot, appState]) => snapshot.hasTranslation && !appState.isCaseProcessing)
	);

	public deleteTranslation() {
		this.appStore.appState$.pipe(
			take(1)
		).subscribe((appState) => {
			if (appState.isCaseProcessing) {
				this.notificationManager.displaySnackBarMessage({ message: 'Cannot delete translated document while case is processed.' });
				return;
			}
			const model = new OkCancelDialogModel();
			model.title = 'Confirm';
			model.message = 'Delete Translated Document?';
			const config = new MatDialogConfig<OkCancelDialogModel>();
			config.data = model;
			this.matDialog.open(OkCancelDialogNewComponent, config).afterClosed().pipe(
				withLatestFrom(this.currentDocument$)
			).subscribe(([okClicked, currentDocument]) => {
				if (okClicked) {
					this.docService.delete(FileType.NormalizedTranslatedFile, currentDocument.docId).subscribe(() => {
						this.notificationManager.displaySnackBarMessage({ message: 'Translated document has been deleted.' });
						this.reviewManager.refreshDocument();
						this.store.dispatch(documentViewActions.viewUpdated({ selectedView: 'pdf' }));
					});
				}
			});
		});
	}

	public openManualTranslationUpload() {
		const model = new ManualUploadDialogModel();
		model.strategy = this.translationUploadStrategy;
		const config = new MatDialogConfig<ManualUploadDialogModel>();
		config.data = model;
		config.width = '450px';
		this.matDialog.open(ManualUploadDialogComponent, config).afterClosed().subscribe(result => {
			if (result) {
				this.reviewManager.refreshDocument();
				this.notificationManager.displaySnackBarMessage({ message: 'Translation doc uploaded.' });
				this.store.dispatch(documentViewActions.viewUpdated({ selectedView: 'translated' }));
			}
		});
	}
}
